/**
 * Administração › Referências oficiais: SNIS/SINISA e bases da ANA por arquivo baixado — Nexora Grid (frontend, US-3701).
 *
 * Criado em: 26/10/2026 · Última modificação: 02/11/2026 · Versão: 1.7.0
 *
 * Decisões de 25/10/2026 (MEL-003): só por download do arquivo oficial (o sistema não acessa a internet) e só para os
 * municípios cadastrados (código IBGE). A equipe Nexora importa para todas as operadoras e indica o endereço de
 * download; a engenharia do cliente importa para a própria. Cada importação é uma versão nova — nada é apagado.
 * "O que falta" lista, por município e fonte, o que ainda não tem referência e onde baixar.
 * v1.1 (US-3703): indicadores oficiais SNIS/SINISA de cada município (último ano, fonte, método e o aviso de quebra).
 * v1.2 (US-3702): outorgas do CNARH com o poço sugerido; a engenharia (`pode_revisar`) confirma ou rejeita; sugestões
 * para a ficha do poço (origem CNARH — quem grava é a engenharia, na ficha).
 * v1.6 (US-3604): "Abrir GeoLibre" (só `editar_ativos`): pede o link de uso único e abre a bancada GIS em outra aba;
 *   lista os projetos guardados e recebe o `.geolibre.json` baixado do GeoLibre (versão nova a cada envio).
 * v1.5 (US-3603): consumo estimado por nó (candidato) de cada município cadastrado (`ConsumoEstimado`).
 * v1.4 (US-3601): Open Buildings, Overture e OpenStreetMap vão à importação das edificações (por município, sem IBGE).
 * v1.3 (US-3705/3706): ficha de segurança hídrica do Atlas Águas 2021 e a chuva dos últimos 30 dias (HidroWeb), com lacunas.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { CabecalhoTela } from "../componentes/Tela";
import { AbasAdmin } from "./AbasAdmin";
import { ConsumoEstimado } from "./ConsumoEstimado";
import { quando } from "./tipos";

type Fonte = { id: string; nome: string; orgao: string; url_download: string; formato: string; licenca: string; citacao: string;
  natureza: string; ultima_carga: { versao: number; ano_referencia: number | null; importado_em: string; linhas_importadas: number } | null };
type Carga = { id: number; fonte: string; versao: number; ano_referencia: number | null; arquivo_nome: string; sha256: string; coluna_ibge: string;
  linhas_lidas: number; linhas_importadas: number; municipios: string[]; importado_por: string; importado_em: string };
type Serie = { municipio: string; indicador: string; nome: string; aviso: string | null;
  pontos: { ano: number; valor: number; unidade: string; fonte: string; metodo: string; versao: number }[] };
type Vinculo = { ativo_id: string; device_id: string | null; distancia_m: number | null; estado: "sugerido" | "confirmado" | "rejeitado"; revisado_por: string | null };
type Outorga = { numero: string; municipio: string; vazao_m3h: number | null; horas_dia: number | null; validade: string | null;
  dias_para_vencer: number | null; finalidade: string; citacao: string; vinculos: Vinculo[] };
type SugestaoFicha = { ativo_id: string; identificacao: string; outorga: string; citacao: string; campos: { campo: string; atual: unknown; cnarh: unknown }[] };
type FichaAtlas = { mananciais: { nome: string; tipo: string | null }[]; sistemas_produtores: string[]; demanda_l_s: Record<string, number>;
  situacao: string[]; citacao: string | null };
type DiaChuva = { municipio: string; estacao: string; data: string; chuva_mm: number | null; lacuna: boolean; fonte: string };
const EDIFICACOES = ["open_buildings", "overture", "osm"];
type ProjetoGis = { nome: string; salvo_por: string; salvo_em: string; versoes: number; url: string };
type Pendencia = { municipio: string; nome: string; fonte: string; nome_fonte: string; url_download: string; acao: string };

export function Referencias() {
  const { usuario, empresa } = useSessao();
  const [fontes, setFontes] = useState<Fonte[]>([]);
  const [cargas, setCargas] = useState<Carga[]>([]);
  const [pendencias, setPendencias] = useState<Pendencia[] | null>(null);
  const [series, setSeries] = useState<Serie[]>([]);
  const [municipios, setMunicipios] = useState<string[]>([]);
  const [atlas, setAtlas] = useState<Record<string, FichaAtlas>>({});
  const [chuva, setChuva] = useState<DiaChuva[]>([]);
  const [outorgas, setOutorgas] = useState<{ outorgas: Outorga[]; sugestoes_ficha: SugestaoFicha[]; pode_revisar: boolean } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [form, setForm] = useState({ fonte: "snis", ano: "", coluna: "", municipio: "" });
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [projetosGis, setProjetosGis] = useState<ProjetoGis[]>([]);
  const [projetoGis, setProjetoGis] = useState<{ nome: string; arquivo: File | null }>({ nome: "", arquivo: null });

  const carregar = useCallback(() => {
    api<Fonte[]>("/v1/referencias/fontes").then(setFontes).catch(() => setFontes([]));
    api<Carga[]>("/v1/referencias/cargas").then(setCargas).catch(() => setCargas([]));
    if (empresa) api<{ pendencias: Pendencia[] }>(`/v1/referencias/pendencias?empresa=${empresa}`).then((r) => setPendencias(r.pendencias)).catch(() => setPendencias([]));
    if (empresa) api<{ municipios: { codigo: string; cadastrado: boolean }[] }>(`/v1/municipios?empresa=${empresa}`)
      .then((r) => setMunicipios(r.municipios.filter((m) => m.cadastrado).map((m) => m.codigo))).catch(() => setMunicipios([]));
    if (empresa) api<{ series: Serie[] }>(`/v1/referencias/indicadores?empresa=${empresa}`).then((r) => setSeries(r.series)).catch(() => setSeries([]));
    if (empresa) api<{ outorgas: Outorga[]; sugestoes_ficha: SugestaoFicha[]; pode_revisar: boolean }>(`/v1/referencias/outorgas?empresa=${empresa}`)
      .then(setOutorgas).catch(() => setOutorgas(null));
    if (empresa) api<{ fichas: Record<string, FichaAtlas> }>(`/v1/referencias/atlas?empresa=${empresa}`).then((r) => setAtlas(r.fichas)).catch(() => setAtlas({}));
    const desde = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    if (empresa) api<{ dias: DiaChuva[] }>(`/v1/referencias/chuva?empresa=${empresa}&inicio=${desde}`).then((r) => setChuva(r.dias)).catch(() => setChuva([]));
    if (empresa && usuario && pode.editarCadastro(usuario))
      api<{ projetos: ProjetoGis[] }>(`/v1/geolibre/projetos?empresa=${empresa}`).then((r) => setProjetosGis(r.projetos)).catch(() => setProjetosGis([]));
  }, [empresa, usuario]);
  useEffect(carregar, [carregar]);
  if (!usuario || !empresa) return null;
  const importa = pode.importarReferencias(usuario);
  const abrirGeolibre = async () => {
    const aba = window.open("about:blank", "_blank");  // abre já no clique (bloqueador de pop-up); o link chega depois
    try {
      const r = await api<{ url: string }>("/v1/geolibre/sessao", { corpo: { empresa } });
      if (aba) { aba.opener = null; aba.location.href = r.url; } else window.location.href = r.url;
    } catch (x) {
      aba?.close();
      setAviso(x instanceof ErroApi ? `GeoLibre: ${x.message}` : "GeoLibre: falha de rede.");
    }
  };
  const guardarProjeto = async (e: FormEvent) => {
    e.preventDefault();
    if (!projetoGis.arquivo) return;
    const dados = new FormData();
    dados.append("arquivo", projetoGis.arquivo);
    try {
      const r = await api<{ url: string }>(`/v1/geolibre/projetos?${new URLSearchParams({ empresa, nome: projetoGis.nome })}`, { form: dados });
      setAviso(`Projeto guardado. No GeoLibre, abra por URL: ${r.url}`);
      setProjetoGis({ nome: "", arquivo: null });
      carregar();
    } catch (x) { setAviso(x instanceof ErroApi ? `Projeto: ${x.message}` : "Projeto: falha de rede."); }
  };

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!arquivo) return;
    const dados = new FormData();
    dados.append("arquivo", arquivo);
    const params = new URLSearchParams({ fonte: form.fonte, empresa });
    if (form.ano) params.set("ano", form.ano);
    if (form.coluna.trim()) params.set("coluna", form.coluna.trim());
    if (form.municipio) params.set("municipio", form.municipio);
    setEnviando(true);
    if (EDIFICACOES.includes(form.fonte)) {  // US-3601: edificações — recorte pela rede do município, sem coluna IBGE
      try {
        if (!form.municipio) throw new ErroApi(422, "informe o município (ex.: costarica)");
        const r = await api<{ versao: number; lidas: number; no_recorte: number; edificacoes: number; pontos: number; com_no: number; licenca: string }>(
          `/v1/edificacoes/importar?${new URLSearchParams({ fonte: form.fonte, empresa, municipio: form.municipio })}`, { form: dados });
        setAviso(`Versão ${r.versao}: ${r.lidas} lidas, ${r.no_recorte} no município (${r.edificacoes} edificações e ${r.pontos} pontos), `
          + `${r.com_no} ligadas a um nó da rede. Licença: ${r.licenca}.`);
        setArquivo(null);
        carregar();
      } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
      finally { setEnviando(false); }
      return;
    }
    try {
      const r = await api<{ versao: number; linhas_lidas: number; linhas_importadas: number; municipios: string[]; coluna_ibge: string | null }>(
        `/v1/referencias/importar?${params}`, { form: dados });
      setAviso(`Versão ${r.versao} importada: ${r.linhas_lidas.toLocaleString("pt-BR")} linhas lidas, ${r.linhas_importadas.toLocaleString("pt-BR")} dos municípios cadastrados`
        + `${r.municipios.length ? ` (${r.municipios.join(", ")})` : " — nenhum município cadastrado no arquivo"}${r.coluna_ibge ? `; código IBGE na coluna "${r.coluna_ibge}"` : ""}.`);
      setArquivo(null);
      carregar();
    } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
    finally { setEnviando(false); }
  };
  const fonteAtual = fontes.find((f) => f.id === form.fonte);
  const revisar = async (o: Outorga, v: Vinculo, confirmar: boolean) => {
    try {
      await api("/v1/referencias/outorgas/vinculo", { corpo: { empresa, numero: o.numero, ativo_id: v.ativo_id, confirmar,
        motivo: confirmar ? "conferido pela engenharia" : "não é este poço" } });
      setAviso(confirmar ? `Outorga ${o.numero} ligada ao poço: a ronda passa a comparar o bombeamento com a outorga.` : `Sugestão da outorga ${o.numero} rejeitada.`);
      carregar();
    } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };

  return (
    <section className="admin tl-pagina">
      <AbasAdmin usuario={usuario} />
      <CabecalhoTela migalhas={[{ texto: NOMES.administracao, para: "/admin/empresas" }, { texto: NOMES.referencias }]} titulo={NOMES.referencias}
        subtitulo="SNIS/SINISA, outorgas e Atlas da ANA, chuva — só por arquivo baixado" />
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      <p className="nx-suave">Dados oficiais dos municípios cadastrados (SNIS/SINISA e ANA). O sistema não baixa nada sozinho: baixe o arquivo no endereço oficial e envie aqui.
        Só ficam as linhas das cidades cadastradas (pelo código IBGE); cada envio é uma versão nova e nada é apagado.</p>

      <h3>Fontes</h3>
      <div className="tabela-rolagem">
        <table className="tabela-composicao" aria-label="Fontes oficiais">
          <thead><tr><th>Fonte</th><th>Órgão</th><th>Onde baixar</th><th>Formato</th><th>Citação</th><th>Última carga</th></tr></thead>
          <tbody>
            {fontes.map((f) => (
              <tr key={f.id}>
                <td><b>{f.nome}</b><br /><small className="nx-suave">{f.natureza}</small></td>
                <td>{f.orgao}</td>
                <td><a href={f.url_download} target="_blank" rel="noreferrer">{f.url_download.replace(/^https:\/\//, "").slice(0, 48)}</a></td>
                <td>{f.formato}</td>
                <td><small>{f.citacao}</small></td>
                <td>{f.ultima_carga ? <>v{f.ultima_carga.versao}{f.ultima_carga.ano_referencia ? ` · ${f.ultima_carga.ano_referencia}` : ""}<br />
                  <small className="nx-suave">{quando(f.ultima_carga.importado_em)}</small></> : <span className="nx-suave">nenhuma</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {importa && (
        <form className="form-filial" aria-label="Importar referência" onSubmit={(e) => void enviar(e)}>
          <h3>Importar arquivo oficial</h3>
          <div className="nx-campo"><label htmlFor="rf-fonte">Fonte</label>
            <select id="rf-fonte" value={form.fonte} onChange={(e) => setForm({ ...form, fonte: e.target.value })}>
              {fontes.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select></div>
          {fonteAtual && <p className="nx-suave">Baixe em <a href={fonteAtual.url_download} target="_blank" rel="noreferrer">{fonteAtual.url_download}</a> ({fonteAtual.formato}).</p>}
          <div className="nx-campo"><label htmlFor="rf-ano">Ano de referência</label>
            <input id="rf-ano" type="number" min={1990} max={2100} value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} /></div>
          <div className="nx-campo"><label htmlFor="rf-coluna">Coluna do código IBGE (vazio = achar sozinho)</label>
            <input id="rf-coluna" maxLength={80} value={form.coluna} placeholder="ex.: id_municipio" onChange={(e) => setForm({ ...form, coluna: e.target.value })} /></div>
          <div className="nx-campo"><label htmlFor="rf-municipio">Arquivo de um município só (séries de estação, sem IBGE)</label>
            <input id="rf-municipio" maxLength={64} value={form.municipio} placeholder="código do município (ex.: costarica)" onChange={(e) => setForm({ ...form, municipio: e.target.value })} /></div>
          <div className="nx-campo"><label htmlFor="rf-arquivo">Arquivo (CSV ou XLSX, até 60 MB)</label>
            <input id="rf-arquivo" type="file" accept=".csv,.xlsx,.txt,.geojson,.json" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} /></div>
          <button type="submit" className="nx-botao" disabled={!arquivo || enviando}>{enviando ? "Importando…" : "Importar"}</button>
        </form>
      )}

      <h3>O que falta nesta operadora</h3>
      {pendencias === null ? <p className="nx-suave">carregando…</p> : (
        <ul aria-label="Referências pendentes">
          {pendencias.map((p) => <li key={`${p.municipio}/${p.fonte}`}><b>{p.nome}</b> · {p.nome_fonte}: {p.acao}</li>)}
          {!pendencias.length && <li className="nx-suave">todos os municípios têm todas as referências</li>}
        </ul>
      )}

      <h3>Indicadores oficiais (SNIS/SINISA)</h3>
      <div className="tabela-rolagem">
        <table className="tabela-composicao" aria-label="Indicadores oficiais">
          <thead><tr><th>Município</th><th>Indicador</th><th>Último ano</th><th>Anos anteriores</th><th>Fonte</th></tr></thead>
          <tbody>
            {series.map((s) => {
              const ultimo = s.pontos[s.pontos.length - 1];
              return (
                <tr key={`${s.municipio}/${s.indicador}`}>
                  <td>{s.municipio}</td>
                  <td><b>{s.indicador}</b> {s.nome}{s.aviso && <><br /><small className="nx-suave" title={s.aviso}>⚠ quebra de série SNIS → SINISA</small></>}</td>
                  <td className="nx-valor">{ultimo.valor.toLocaleString("pt-BR")} {ultimo.unidade} ({ultimo.ano})</td>
                  <td><small>{s.pontos.slice(0, -1).map((p) => `${p.ano}: ${p.valor.toLocaleString("pt-BR")}`).join(" · ") || "—"}</small></td>
                  <td><small>{ultimo.fonte.toUpperCase()} (declarado pelo prestador)</small></td>
                </tr>
              );
            })}
            {!series.length && <tr><td colSpan={5} className="nx-suave">nenhum indicador importado ainda (importe o arquivo do SNIS ou do SINISA)</td></tr>}
          </tbody>
        </table>
      </div>

      <h3>Outorgas (CNARH)</h3>
      <div className="tabela-rolagem">
        <table className="tabela-composicao" aria-label="Outorgas">
          <thead><tr><th>Outorga</th><th>Município</th><th>Vazão outorgada</th><th>Validade</th><th>Poço</th></tr></thead>
          <tbody>
            {(outorgas?.outorgas ?? []).map((o) => (
              <tr key={o.numero}>
                <td><b>{o.numero}</b><br /><small className="nx-suave">{o.finalidade}</small></td>
                <td>{o.municipio}</td>
                <td className="nx-valor">{o.vazao_m3h === null ? "—" : `${o.vazao_m3h.toLocaleString("pt-BR")} m³/h`}{o.horas_dia ? ` · ${o.horas_dia} h/dia` : ""}</td>
                <td>{o.validade ? o.validade.split("-").reverse().join("/") : "—"}{o.dias_para_vencer !== null && o.dias_para_vencer <= 90 &&
                  <><br /><small className="erro">{o.dias_para_vencer < 0 ? "vencida" : `vence em ${o.dias_para_vencer} dias`}</small></>}</td>
                <td>{o.vinculos.length ? o.vinculos.map((v) => (
                  <div key={v.ativo_id}>{v.device_id ?? v.ativo_id} · {v.estado}{v.distancia_m !== null ? ` (${Math.round(v.distancia_m)} m)` : ""}
                    {outorgas?.pode_revisar && v.estado === "sugerido" && <> <button type="button" className="nx-botao" onClick={() => void revisar(o, v, true)}>Confirmar</button>
                      <button type="button" className="nx-botao" onClick={() => void revisar(o, v, false)}>Rejeitar</button></>}</div>
                )) : <span className="nx-suave">nenhum poço a até 1 km</span>}</td>
              </tr>
            ))}
            {!outorgas?.outorgas.length && <tr><td colSpan={5} className="nx-suave">nenhuma outorga importada (importe a planilha do CNARH)</td></tr>}
          </tbody>
        </table>
      </div>
      {!!outorgas?.sugestoes_ficha.length && (
        <div className="nx-cartao" role="region" aria-label="Sugestões para a ficha do poço">
          <h4>Sugestões para a ficha do poço (origem CNARH)</h4>
          <ul>{outorgas.sugestoes_ficha.map((s) => (
            <li key={s.ativo_id}><b>{s.identificacao}</b> · outorga {s.outorga}: {s.campos.map((c) => `${c.campo} ${c.atual ?? "vazio"} → ${c.cnarh}`).join("; ")}
              <br /><small className="nx-suave">{s.citacao}. Confira e grave na ficha do poço (Cadastro de ativos).</small></li>
          ))}</ul>
        </div>
      )}

      <h3>Segurança hídrica (Atlas Águas 2021)</h3>
      {Object.keys(atlas).length ? Object.entries(atlas).map(([m, f]) => (
        <div key={m} className="nx-cartao" role="region" aria-label={`Atlas Águas — ${m}`}>
          <b>{m}</b>: {f.situacao.join("; ") || "situação não informada"}
          <ul>
            <li>Mananciais: {f.mananciais.map((x) => `${x.nome}${x.tipo ? ` (${x.tipo})` : ""}`).join(", ") || "—"}</li>
            <li>Sistemas produtores: {f.sistemas_produtores.join(", ") || "—"}</li>
            <li>Demanda projetada: {Object.entries(f.demanda_l_s).map(([ano, v]) => `${ano}: ${v.toLocaleString("pt-BR")} L/s`).join(" · ") || "—"}</li>
          </ul>
          <small className="nx-suave">{f.citacao}</small>
        </div>
      )) : <p className="nx-suave">nenhuma ficha do Atlas importada</p>}

      <h3>Chuva nos últimos 30 dias (HidroWeb)</h3>
      {chuva.length ? (
        <ul aria-label="Chuva por estação">
          {[...new Set(chuva.map((d) => `${d.municipio}|${d.estacao}`))].map((chave) => {
            const dias = chuva.filter((d) => `${d.municipio}|${d.estacao}` === chave);
            const total = dias.reduce((s, d) => s + (d.chuva_mm ?? 0), 0);
            return <li key={chave}><b>{dias[0].municipio}</b> · {dias[0].fonte}: {total.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mm em {dias.length} dias
              {dias.some((d) => d.lacuna) ? ` (${dias.filter((d) => d.lacuna).length} dia(s) sem leitura)` : ""}</li>;
          })}
        </ul>
      ) : <p className="nx-suave">nenhuma série de chuva nos últimos 30 dias</p>}

      {pode.editarCadastro(usuario) && (
        <section className="nx-cartao" aria-label="GeoLibre">
          <h3>GeoLibre (bancada GIS da engenharia)</h3>
          <p className="nx-suave">Abre o GeoLibre em outra aba, com o seu login, para preparar e conferir camadas da rede EPANET e das edificações.
            Os dados são só desta operadora; os projetos salvos ficam guardados com versões.</p>
          <button type="button" className="nx-botao" onClick={() => void abrirGeolibre()}>Abrir GeoLibre</button>
          <p className="nx-suave">Camadas para adicionar no GeoLibre por URL: <code>/dados/rede.geojson</code> e <code>/dados/edificacoes.geojson</code>.
            O GeoLibre baixa o projeto ao salvar; guarde o <code>.geolibre.json</code> aqui e reabra no GeoLibre por URL.</p>
          {projetosGis.length ? (
            <ul aria-label="Projetos do GeoLibre">{projetosGis.map((pg) => (
              <li key={pg.nome}><code>{pg.url}</code> · {pg.versoes} versão(ões) · última por {pg.salvo_por} em {quando(pg.salvo_em)}</li>
            ))}</ul>
          ) : <p className="nx-suave">nenhum projeto guardado</p>}
          <form className="form-filial" aria-label="Guardar projeto do GeoLibre" onSubmit={(e) => void guardarProjeto(e)}>
            <div className="nx-campo"><label htmlFor="gis-nome">Nome do projeto</label>
              <input id="gis-nome" required pattern="[a-z0-9][a-z0-9_\-]{0,63}" value={projetoGis.nome} placeholder="ex.: setores-costarica"
                onChange={(e) => setProjetoGis({ ...projetoGis, nome: e.target.value })} /></div>
            <div className="nx-campo"><label htmlFor="gis-arquivo">Arquivo .geolibre.json</label>
              <input id="gis-arquivo" type="file" accept=".json,application/json" required
                onChange={(e) => setProjetoGis({ ...projetoGis, arquivo: e.target.files?.[0] ?? null })} /></div>
            <button type="submit" className="nx-botao">Guardar projeto</button>
          </form>
        </section>
      )}

      {municipios.map((m) => <ConsumoEstimado key={m} empresa={empresa} municipio={m} />)}

      <h3>Histórico de cargas</h3>
      <div className="tabela-rolagem">
        <table className="tabela-composicao" aria-label="Histórico de cargas">
          <thead><tr><th>Fonte</th><th>Versão</th><th>Ano</th><th>Arquivo</th><th>Linhas (lidas → importadas)</th><th>Municípios</th><th>Quem</th><th>Quando</th></tr></thead>
          <tbody>
            {cargas.map((c) => (
              <tr key={c.id}>
                <td>{c.fonte}</td><td>v{c.versao}</td><td>{c.ano_referencia ?? "—"}</td>
                <td title={`sha256 ${c.sha256} · coluna ${c.coluna_ibge}`}>{c.arquivo_nome}</td>
                <td className="nx-valor">{c.linhas_lidas.toLocaleString("pt-BR")} → {c.linhas_importadas.toLocaleString("pt-BR")}</td>
                <td><small>{c.municipios.join(", ") || "—"}</small></td><td>{c.importado_por}</td><td>{quando(c.importado_em)}</td>
              </tr>
            ))}
            {!cargas.length && <tr><td colSpan={8} className="nx-suave">nenhum arquivo importado ainda</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
